/**
 * Table row for a field event summary.
 */
import * as React from 'react';
import { Calendar, Building2, BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import { EVENT_SUMMARY_STATUS_CONFIG } from './event-summary-configs';
import type { EventSummary } from '@/types/event-summary.types';
import {
  formatSummaryEventDate,
  deriveSummaryActivityName,
  formatSummaryFilledDate,
} from './event-summary-table-utils';

/** Props for {@link EventSummaryRow}. */
interface EventSummaryRowProps {
  summary: EventSummary;
  onView?: (summaryId: string) => void;
  onEdit?: () => void;
  pageActions?: { canEditRow?: boolean; canViewRow?: boolean };
}

/** Table row for a field event summary. */
export const EventSummaryRow: React.FC<EventSummaryRowProps> = ({
  summary,
  onView,
  onEdit,
  pageActions,
}) => {
  const canEdit = getRowPermission(summary, 'action-edit-row', pageActions?.canEditRow ?? true);
  const canView = getRowPermission(summary, 'action-view-row', pageActions?.canViewRow ?? true);

  const statusConfig =
    EVENT_SUMMARY_STATUS_CONFIG[summary.status] || EVENT_SUMMARY_STATUS_CONFIG.not_filled;
  const StatusIcon = statusConfig.icon;

  const eventDisplayText = formatSummaryEventDate(summary);
  const activityName = deriveSummaryActivityName(summary);
  const filledDateText = formatSummaryFilledDate(summary.filledAt);

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="font-medium text-foreground">{eventDisplayText}</span>
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground">
            {summary.event?.establishment?.name || '—'}
          </span>
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground">{activityName}</span>
        </div>
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td>
        <span className="text-sm text-foreground">{filledDateText}</span>
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          {canView && <Button preset="row-view" onClick={() => onView?.(summary.id)} />}

          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.()} />}
        </div>
      </td>
    </TableRow>
  );
};
